'use client';
import { useRef } from 'react';
import Image from 'next/image';
import gradleTile from './GradleTile.png';
import mavenTile from './MavenTile.png';
import sbtTile from './SbtTile.png';
import Link from 'next/link';
import { useGSAP } from '@gsap/react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

export default function RunBuildScan() {
  const containerRef = useRef<HTMLElement>(null);

  useGSAP(
    () => {
      const container = containerRef.current;
      if (!container) return;

      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: container,
          start: 'top 55%',
          end: 'bottom 20%',
          toggleActions: 'restart pause restart pause',
        },
      });

      tl.to('.build-scan-tile', { scale: 1.05, stagger: { each: 0.15, from: 'start' }, duration: 0.3, delay: 0.2 }).to(
        '.build-scan-tile',
        { scale: 1, duration: 0.3 },
      );
    },
    { scope: containerRef },
  );

  return (
    <section ref={containerRef} className="py-section bg-gray-1 px-4 text-white md:px-10">
      <div className="max-w-section relative rounded-3xl bg-gradient-to-r from-[#0C5A73] to-[#0C5A73] bg-[length:400%_400%] bg-[position:100%_0%] p-10 text-white before:absolute before:inset-0 before:rounded-3xl before:bg-[radial-gradient(25%_100%_at_100%_0%,#209BC4,transparent)] before:content-['']">
        <div className="absolute bottom-0 right-0 z-0 hidden overflow-clip rounded-3xl md:block">
          <GraphSvg />
        </div>
        <div className="relative z-10">
          <h2 className="text-h4 mb-2 lg:mb-4">Run a free Build Scan</h2>
          <p className="text-body mb-8 md:mb-10">Choose a build system to learn how to get started.</p>
          <div className="flex items-center justify-center gap-4">
            <Link href="https://scans.gradle.com/#gradle" target="_blank" className="build-scan-tile shrink-0">
              <Image
                src={gradleTile}
                alt="Run a build scan with Gradle"
                className="size-20 focus-within:scale-105 hover:scale-105 md:size-32"
              />
            </Link>
            <Link href="https://scans.gradle.com/#maven" target="_blank" className="build-scan-tile shrink-0">
              <Image
                src={mavenTile}
                alt="Run a build scan with Maven"
                className="size-20 focus-within:scale-105 hover:scale-105 md:size-32"
              />
            </Link>
            <Link href="https://scans.gradle.com/#sbt" target="_blank" className="build-scan-tile shrink-0">
              <Image
                src={sbtTile}
                alt="Run a build scan with SBT"
                className="size-20 focus-within:scale-105 hover:scale-105 md:size-32"
              />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

function GraphSvg() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="312" height="241" viewBox="0 0 312 241" fill="none">
      <g style={{ mixBlendMode: 'color-dodge' }} filter="url(#filter0_i_11940_662)">
        <path
          d="M0.000488281 255.814C0.000488281 245.284 8.53713 236.747 19.0676 236.747C29.5981 236.747 38.1347 245.284 38.1347 255.814V260.609C38.1347 271.139 29.5981 279.676 19.0676 279.676C8.53713 279.676 0.000488281 271.139 0.000488281 260.609L0.000488281 255.814ZM272.979 260.609C272.979 271.139 281.516 279.676 292.046 279.676C302.577 279.676 311.113 271.139 311.113 260.609V231.902C311.113 221.371 302.577 212.834 292.046 212.834C281.516 212.834 272.979 221.371 272.979 231.902V260.609ZM204.734 260.609C204.734 271.139 213.271 279.676 223.802 279.676C234.332 279.676 242.869 271.139 242.869 260.609V73.4359C242.869 62.9054 234.332 54.3687 223.802 54.3687C213.271 54.3687 204.734 62.9054 204.734 73.4358V260.609ZM136.49 260.609C136.49 271.139 145.027 279.676 155.557 279.676C166.087 279.676 174.624 271.139 174.624 260.609V19.8991C174.624 9.36864 166.087 0.832031 155.557 0.832031C145.027 0.832031 136.49 9.36862 136.49 19.899V260.609ZM68.2452 260.609C68.2452 271.139 76.7819 279.676 87.3123 279.676C97.8428 279.676 106.379 271.139 106.379 260.609V123.24C106.379 112.709 97.8428 104.173 87.3123 104.173C76.7819 104.173 68.2452 112.709 68.2452 123.24V260.609Z"
          fill="url(#paint0_linear_11940_662)"
        />
      </g>
      <defs>
        <filter
          id="filter0_i_11940_662"
          x="0.000488281"
          y="0.832031"
          width="311.113"
          height="278.844"
          filterUnits="userSpaceOnUse"
          colorInterpolationFilters="sRGB"
        >
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape" />
          <feColorMatrix
            in="SourceAlpha"
            type="matrix"
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
            result="hardAlpha"
          />
          <feOffset />
          <feGaussianBlur stdDeviation="9.09273" />
          <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1" />
          <feColorMatrix type="matrix" values="0 0 0 0 0.74902 0 0 0 0 0.894118 0 0 0 0 0.941176 0 0 0 1 0" />
          <feBlend mode="normal" in2="shape" result="effect1_innerShadow_11940_662" />
        </filter>
        <linearGradient
          id="paint0_linear_11940_662"
          x1="155.557"
          y1="0.832031"
          x2="155.557"
          y2="270.639"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#BFE4F0" stopOpacity="0" />
          <stop offset="1" stopColor="#BFE4F0" stopOpacity="0.64" />
        </linearGradient>
      </defs>
    </svg>
  );
}
